import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import Captcha from '../../components/Captcha';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

export default function ForgotPassword() {
  const { settings } = useSettings();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (event) => {
    event.preventDefault();
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Please enter your registered email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!isCaptchaValid) {
      newErrors.captcha = 'Please enter the exact 5-character Captcha code';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all highlighted required fields');
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success(data.message || 'Password reset link sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not send the reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#FAF8F5] px-4 pt-3 pb-8 sm:pt-6 sm:pb-12 text-[#2B1810] flex items-center justify-center">
      <div className="w-full max-w-md bg-white border border-[#E8DFD8] rounded-2xl p-5 sm:p-7 shadow-xl space-y-5">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <Link to="/" className="inline-block">
            <img
              src={resolveMedia(settings?.logo || DEFAULT_LOGO)}
              alt={settings?.siteName || 'Chandana Aura'}
              className="h-11 mx-auto object-contain"
            />
          </Link>
          <div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#C49A45] bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full inline-block">
              Account Recovery
            </span>
            <h1 className="font-heading text-2xl font-bold text-[#2B1810] mt-1">
              Reset Your Password
            </h1>
            <p className="text-xs text-[#8A796C]">
              Enter your registered email and we will send a secure reset link.
            </p>
          </div>
        </div>

        {sent ? (
          <div className="text-center py-4 space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xl font-bold">
              ✓
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-[#2B1810]">
                Check Your Inbox
              </h2>
              <p className="text-xs text-[#76675E] mt-1 leading-relaxed">
                A password reset link has been dispatched to <strong>{email}</strong>. It will expire in 30 minutes.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setIsCaptchaValid(false);
                setErrors({});
              }}
              className="text-xs font-bold text-[#C49A45] hover:underline uppercase tracking-wider"
            >
              Try Another Email
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#2B1810] mb-1.5">
                Registered Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className={`w-full h-11 px-3.5 pl-10 text-xs rounded-lg border transition-all font-medium ${
                    errors.email
                      ? 'border-red-500 bg-red-50/20 text-red-900 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                      : 'border-[#D9D0C8] bg-[#FAF8F5] text-[#2B1810] placeholder-[#A0938A] focus:bg-white focus:border-[#C49A45] focus:ring-1 focus:ring-[#C49A45]'
                  } focus:outline-none`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                  }}
                />
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A796C]">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                </span>
              </div>
              {errors.email && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.email}
                </p>
              )}
            </div>

            {/* Captcha Security Check */}
            <div className="pt-1">
              <Captcha
                error={Boolean(errors.captcha)}
                onValidate={(valid) => {
                  setIsCaptchaValid(valid);
                  if (valid && errors.captcha) setErrors((prev) => ({ ...prev, captcha: '' }));
                }}
              />
              {errors.captcha && (
                <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.captcha}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#2B1810] hover:bg-[#C49A45] text-white font-bold text-xs uppercase tracking-widest rounded-lg shadow-md hover:shadow-lg transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? 'SENDING RESET LINK...' : 'SEND SECURE RESET LINK'}
            </button>
          </form>
        )}

        {/* Back to Login Link */}
        <div className="pt-4 border-t border-[#E8DFD8] text-center">
          <Link
            to="/login"
            className="text-xs font-bold text-[#8A796C] hover:text-[#C49A45] transition-colors inline-flex items-center gap-1.5"
          >
            <span>←</span> Back to Sign In
          </Link>
        </div>

      </div>
    </div>
  );
}
